import * as React from 'react';
import { cn } from '@/core/lib/utils';
import { FileText, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface InsuranceClaim {
  id: string;
  expenseId: string;
  description: string;
  amount: number;
  status: 'pending' | 'submitted' | 'approved' | 'denied' | 'partial';
  submittedDate?: string;
  resolvedDate?: string;
  reimbursedAmount?: number;
}

interface InsuranceClaimTrackerProps {
  claims?: InsuranceClaim[];
  onSubmitClaim?: (expenseId: string) => void;
  className?: string;
}

const statusConfig = {
  pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Pending' },
  submitted: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Submitted' },
  approved: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Approved' },
  denied: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Denied' },
  partial: { icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Partial' },
};

export function InsuranceClaimTracker({ 
  claims = [], 
  onSubmitClaim,
  className 
}: InsuranceClaimTrackerProps) {
  if (claims.length === 0) {
    return (
      <div className={cn('p-6 text-center text-gray-500', className)}>
        <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p>No insurance claims to track</p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {claims.map((claim) => {
        const config = statusConfig[claim.status];
        const Icon = config.icon;

        return (
          <div
            key={claim.id}
            className="flex items-center justify-between p-4 bg-white rounded-lg border"
          >
            <div className="flex items-center gap-3">
              <div className={cn('p-2 rounded-full', config.bg)}>
                <Icon className={cn('h-4 w-4', config.color)} />
              </div>
              <div>
                <p className="font-medium text-gray-900">{claim.description}</p>
                <p className="text-sm text-gray-500">
                  {'$' + claim.amount.toFixed(2)}
                  {claim.reimbursedAmount && ' � Reimbursed: $' + claim.reimbursedAmount.toFixed(2)}
                </p>
              </div>
            </div>
            <span className={cn('px-3 py-1 rounded-full text-sm font-medium', config.bg, config.color)}>
              {config.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default InsuranceClaimTracker;
